import useLocalStorage from '@app/src/hooks/localStorage'
import React, { createContext, useCallback, useContext, useState } from 'react'

export const LOCAL_STORAGE_NAV_PINNED = 'wf_nav_pinned'
const DEFAULT_IS_PINNED = true

type VerticalMenuPinContextValue = {
  isPinned: boolean
  setIsPinned: (pinned: boolean) => void
}

const VerticalMenuPinContext = createContext<VerticalMenuPinContextValue>({
  isPinned: DEFAULT_IS_PINNED,
  setIsPinned: () => undefined,
})

export const VerticalMenuPinProvider = ({ children }: { children: React.ReactNode }) => {
  const [isPinned, setIsPinned] = useLocalStorage<boolean>(LOCAL_STORAGE_NAV_PINNED, DEFAULT_IS_PINNED)
  return <VerticalMenuPinContext.Provider value={{ isPinned, setIsPinned }}>{children}</VerticalMenuPinContext.Provider>
}

export const useVerticalMenuPin = () => useContext(VerticalMenuPinContext)

type VerticalMenuContextValue = {
  isExpanded: boolean
  isStableExpanded: boolean
  onPopoverOpen: () => void
  onPopoverClose: () => void
  openFlyoutId: string | null
  openFlyout: (id: string) => void
  closeFlyout: (id: string) => void
}

const VerticalMenuContext = createContext<VerticalMenuContextValue>({
  isExpanded: true,
  isStableExpanded: true,
  onPopoverOpen: () => undefined,
  onPopoverClose: () => undefined,
  openFlyoutId: null,
  openFlyout: () => undefined,
  closeFlyout: () => undefined,
})

export const VerticalMenuProvider = ({
  isExpanded,
  isStableExpanded,
  onPopoverOpen,
  onPopoverClose,
  children,
}: {
  isExpanded: boolean
  isStableExpanded: boolean
  onPopoverOpen: () => void
  onPopoverClose: () => void
  children: React.ReactNode
}) => {
  const [openFlyoutId, setOpenFlyoutId] = useState<string | null>(null)

  const openFlyout = useCallback((id: string) => setOpenFlyoutId(id), [])

  const closeFlyout = useCallback((id: string) => setOpenFlyoutId(current => (current === id ? null : current)), [])

  return (
    <VerticalMenuContext.Provider
      value={{ isExpanded, isStableExpanded, onPopoverOpen, onPopoverClose, openFlyoutId, openFlyout, closeFlyout }}
    >
      {children}
    </VerticalMenuContext.Provider>
  )
}

export const useVerticalMenu = () => useContext(VerticalMenuContext)
